import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ErrorBoundary } from "@shared/components";
import { useLoader } from "@shared/hooks";
import { LEADERBOARD_BOARD, LEADERBOARD_PERIOD } from "@shared/models";
import { selectors as SharedSelectors } from "@shared/store";
import { actions as SparksActions, selectors as SparksSelectors } from "@containers/Recognition/store/sparks";

// Relative, not the "@containers/Recognition/components" alias: jest resolves no path aliases, and
// this component's own suite needs the REAL toggles/load-status rendering (the "toggles are local
// state, never the tab's" test clicks the real buttons; the load-status test asserts the real
// settled-guard) rather than stubbed markers -- same reasoning as ValueLadderCards' own relative
// import of CardHead, and LeaderboardsContainer's own relative import of LeaderboardLoadStatus.
import { LeaderboardBoardToggle } from "../../Leaderboard/LeaderboardBoardToggle";
import { LEADERBOARD_LOAD_FAILED_MESSAGE, LeaderboardLoadStatus } from "../../Leaderboard/LeaderboardLoadStatus";
// Design-fix plan item 4: shared with LeaderboardsContainer.tsx now, rather than this card hardcoding
// its own board-only, period-blind copy ("...yet." for every period). leaderboardViewerUnrankedShortCopy
// is this card's own short form of the tab's leaderboardViewerUnrankedCopy -- see its own JSDoc.
import { leaderboardEmptyCopy, leaderboardViewerUnrankedShortCopy } from "../../Leaderboard/leaderboardEmptyCopy";
// Also from the Leaderboard folder's own shelf, once 923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md's L1 made the
// Leaderboard tab a second consumer of both -- same relative-import reasoning as the three above.
import { PodiumTile, PODIUM_ORDER } from "../../Leaderboard/PodiumTile";
import { RankRow } from "../../Leaderboard/RankRow";
// The is-me rule, shared with LeaderboardsContainer.tsx rather than spelled out at each of this file's
// three call sites below -- same shelf, same relative-import reasoning as the imports above.
import { isViewerRow } from "../../Leaderboard/isViewerRow";
// This card's period dropdown and the Leaderboard tab's are ONE component now (they were briefly two
// near-identical copies, `ScopeSelect` here and `LeaderboardPeriodToggle` there, which meant two
// copies of the four period labels). Same shelf, same relative-import reasoning as above.
import { LeaderboardPeriodSelect } from "../../Leaderboard/LeaderboardPeriodSelect";
import "./index.scss";

// overview-leaderboard-card-plan.md Part 2: "Fetch limit: 10 -- exactly the rows rendered." The
// viewer's own rank now comes from the api's `viewer` field, so there is no reason to over-fetch.
const OVERVIEW_LEADERBOARD_ROWS = 10;

interface OverviewLeaderboardProps {
  // loader-dedup plan (single-loader Overview fix): forwarded verbatim to `LeaderboardLoadStatus`'s
  // own `suppressLoader` -- MySparksContainer.tsx passes ITS OWN page-level `isLoading` here so this
  // card's loader does not stack on top of the page loader while both are in flight. Defaults to false
  // (show) -- see LeaderboardLoadStatus's own comment for what "unaffected by construction" means for
  // this component's other consumer.
  suppressLoader?: boolean;
}

/**
 * The Overview's Leaderboard rail card (Figma `Leaderboard`, 9119:1089, in the Overview v4 frame's
 * right rail, 9123:1171). Self-contained: owns its own board/period toggle state (`useState`, NOT the
 * slice's `leaderboardBoard`/`leaderboardPeriod` -- those belong to the Leaderboard TAB and reusing
 * them would re-couple this card to it), and its own complete data lifecycle -- its own action triad
 * (`getOverviewLeaderboard`), its own `takeLatest` saga slot, its own `useLoader` name, and its own
 * slice fields (`overviewLeaderboardResults`/`IsLoaded`/`LoadFailed`), reset by `clearSparks` (this
 * card's own logged-in-user lifecycle) rather than the tab's `clearLeaderboard`.
 *
 * Renders: a head (title + the shared `LeaderboardPeriodSelect` pill -- Figma's single-pill control,
 * rendered in its BASE form here, while the Leaderboard tab passes its own wider modifier class), the
 * board toggle (also shared with the tab and restyled via a modifier class, never rebuilt, so this
 * card owns neither control's markup), a podium for ranks 1-3 in #2/#1/#3 order, plain rows
 * for ranks 4-10, the pinned "you" row (from the api's own `viewer`, suppressed when that viewer
 * already appears among the rendered rows, using the logged-in user's name since `viewer` itself
 * carries no name), and a footer stating how many points move the viewer up a place -- or, in that same
 * footer slot when the viewer has no score at all this board/period, a short line stating just that
 * fact (the tab's own line says more; see leaderboardViewerUnrankedShortCopy's own JSDoc for why this
 * card's is shorter). A row/tile that
 * IS the viewer's own (the shared `isViewerRow` predicate) is marked `isViewer` wherever it renders --
 * podium, inline row, or pinned row -- one convention, not three. Own content wrapped in an `ErrorBoundary`
 * (same import `LeaderboardsContainer.tsx` uses) so a render failure here shows a friendly inline
 * message without blanking the rest of the Overview tab.
 */
const OverviewLeaderboard: FC<OverviewLeaderboardProps> = ({ suppressLoader }) => {
  const dispatch = useDispatch();

  // D4 of the plan: LOCAL to this card, never the slice's leaderboardBoard/leaderboardPeriod (those
  // are written by the Leaderboard tab's own toggles). Narrowed to RECEIVED|GIVEN -- this card, like
  // the tab's own toggle, never offers CROWNS.
  const [board, setBoard] = useState<LEADERBOARD_BOARD.RECEIVED | LEADERBOARD_BOARD.GIVEN>(LEADERBOARD_BOARD.RECEIVED);
  // leaderboard-default-quarter: this card's own default -- Figma reads quarterly throughout ("This
  // quarter", "Resets in 18 days · new quarter 1 Oct", "Best quarter"), matching the Leaderboard tab's
  // own default (reducer.tsx's initialState.leaderboardPeriod) even though this state is local, not
  // read from the store.
  const [period, setPeriod] = useState<LEADERBOARD_PERIOD>(LEADERBOARD_PERIOD.QUARTER);

  const data = useSelector(SparksSelectors.getOverviewLeaderboardResults());
  const loadFailed = useSelector(SparksSelectors.getOverviewLeaderboardLoadFailed());
  // Design-fix plan item 3: whether a fetch has ever actually SETTLED (flips true only on
  // getOverviewLeaderboard.success, reducer.tsx) -- read below by showEmpty so the empty copy can't
  // paint before a fetch has even started.
  const isLoaded = useSelector(SparksSelectors.getOverviewLeaderboardIsLoaded());
  // The pinned "you" row's own name -- `viewer` (above) carries rank/total_score/points_to_next_rank
  // only, no name, so the logged-in user's own details (the app-wide viewer selector, same call site
  // LeaderboardsContainer.tsx already uses for its own is-me highlight) supply it instead.
  const viewerUser = useSelector(SharedSelectors.getUserDetails());

  // Its own useLoader name (D of the plan) -- sharing "LeaderboardResults" would tie this card's
  // loading state to the Leaderboard tab's own fetch.
  const { isLoading } = useLoader({
    name: "OverviewLeaderboard",
    actionTypes: [SparksActions.getOverviewLeaderboard],
  });

  // `handleBoardChange` takes the toggle's own full LEADERBOARD_BOARD parameter type (it never
  // actually offers CROWNS, but its prop type is the full enum, matching LeaderboardsContainer's own
  // handler) and narrows before writing the local, RECEIVED|GIVEN-only state above.
  const handleBoardChange = (nextBoard: LEADERBOARD_BOARD) => {
    if (nextBoard === LEADERBOARD_BOARD.RECEIVED || nextBoard === LEADERBOARD_BOARD.GIVEN) {
      setBoard(nextBoard);
    }
  };

  // Own fetch effect, keyed on this card's OWN local board/period -- a toggle click here only ever
  // dispatches getOverviewLeaderboard.request, never the tab's setLeaderboardBoard/setLeaderboardPeriod.
  useEffect(() => {
    dispatch(
      SparksActions.getOverviewLeaderboard.request(
        board === LEADERBOARD_BOARD.GIVEN
          ? { board: LEADERBOARD_BOARD.GIVEN, period, limit: OVERVIEW_LEADERBOARD_ROWS }
          : { board: LEADERBOARD_BOARD.RECEIVED, period, limit: OVERVIEW_LEADERBOARD_ROWS },
      ),
    );
  }, [dispatch, board, period]);

  const results = data?.results ?? [];
  const viewer = data?.viewer ?? null;
  const leaderScore = results[0]?.total_score ?? 0;
  // The exact window the podium + rank rows below render from -- reused below to decide whether the
  // pinned "you" row would be a DUPLICATE of a row already on screen.
  const renderedResults = results.slice(0, OVERVIEW_LEADERBOARD_ROWS);
  const podiumEntries = renderedResults.slice(0, 3);
  const rankRows = renderedResults.slice(3, OVERVIEW_LEADERBOARD_ROWS);
  // Matched through the shared `isViewerRow` predicate -- see its own comment for why the choice
  // between the row's top-level `user_id` and its nested `user.id` is written down in one place rather
  // than at each of the three call sites here and the three in LeaderboardsContainer.tsx.
  const viewerAlreadyVisible = renderedResults.some((row) => isViewerRow(row, viewerUser));

  // Requires isLoaded, not just !isLoading -- on mount, isLoading (from useLoader) stays false until
  // the dispatched request's own passive effect is even observed, so without isLoaded the very FIRST
  // committed frame could paint this empty copy before the fetch has even started (a real landing-
  // screen flash, not a hypothetical). isLoaded flips true only on getOverviewLeaderboard.success
  // (reducer.tsx), so a fetch that's merely in flight or has failed never satisfies this clause on its
  // own -- !isLoading/!loadFailed below still do their own separate jobs alongside it, same as
  // LeaderboardsContainer.tsx's own showResultsEmptyState guard.
  const showEmpty = isLoaded && results.length === 0 && !isLoading && !loadFailed;
  // Gates ALL THREE bands below: the podium/rows, the pinned "you" row, and the footer. While
  // loading, LeaderboardLoadStatus's own centered Loader takes their place entirely (plan: "a centred
  // Loader, in place of the podium and rows"). Previously only the podium/rows were gated here -- a
  // board/period refetch could render the Loader with the PREVIOUS period's pinned row and footer
  // still underneath it, while the period pill had already relabelled to the NEW period, so the footer's
  // "N and you move up a place" stated a false fact about the period the viewer was now looking at --
  // not just a cosmetic flash of stale content.
  const showBoard = !isLoading && results.length > 0;

  return (
    <ErrorBoundary>
      <div className="overview-leaderboard">
        {/* This is the one head on the screen that is NOT CardHead (its right slot is a link-or-caption
            discriminated union, deliberately incompatible with an arbitrary period control) -- so this
            reuses CardHead's own title/rule STYLING (the .spark-card-head/.spark-card-head-title
            classes) without importing the component itself. */}
        <div className="spark-card-head overview-leaderboard-head">
          <div className="spark-card-head-title">Leaderboard</div>
          <LeaderboardPeriodSelect period={period} onChange={setPeriod} />
        </div>
        <LeaderboardBoardToggle board={board} onChange={handleBoardChange} className="overview-leaderboard-toggle" />
        <LeaderboardLoadStatus
          isLoading={isLoading}
          loadFailed={loadFailed}
          failureTestId="overview-leaderboard-load-failed"
          failureMessage={LEADERBOARD_LOAD_FAILED_MESSAGE}
          suppressLoader={suppressLoader}
        />
        {showEmpty ? (
          <div className="overview-leaderboard-empty" data-testid="overview-leaderboard-empty">
            {leaderboardEmptyCopy(board, period)}
          </div>
        ) : null}
        {showBoard ? (
          <>
            <div className="overview-leaderboard-podium">
              {PODIUM_ORDER.map((place) => {
                const entry = podiumEntries[place - 1];
                if (!entry) return null;
                // Design-fix plan item 2: marks the viewer's own podium tile the same way the pinned
                // row and the inline rank rows below do -- same shared predicate as
                // viewerAlreadyVisible above.
                return (
                  <PodiumTile
                    key={place}
                    place={place}
                    firstName={entry.user.first_name}
                    lastName={entry.user.last_name}
                    imageUrl={entry.user.avatar_url}
                    totalScore={entry.total_score}
                    isViewer={isViewerRow(entry, viewerUser)}
                  />
                );
              })}
            </div>
            <div className="overview-leaderboard-rows">
              {/* Design-fix plan item 2: a viewer inside ranks 4-10 previously had no "that's you"
                  marker anywhere once the duplicate pinned row was suppressed -- the SAME `isViewer`
                  treatment the pinned row below uses, so the two read as one convention rather than
                  two different ways of saying the same thing. */}
              {rankRows.map((entry, index) => (
                <RankRow
                  key={entry.user_id}
                  rank={index + 4}
                  firstName={entry.user.first_name}
                  lastName={entry.user.last_name}
                  imageUrl={entry.user.avatar_url}
                  totalScore={entry.total_score}
                  leaderScore={leaderScore}
                  isViewer={isViewerRow(entry, viewerUser)}
                />
              ))}
            </div>
          </>
        ) : null}
        {/* The pinned "you" row -- rendered from the api's OWN viewer.rank, never this array's index
            (the mutation the plan names: an index would be identical to viewer.rank for a viewer inside
            the top 10, so this row only proves itself right for a viewer OUTSIDE it). Requires
            viewerUser too, purely defensively -- this container never renders without a logged-in user
            in practice (it sits behind an auth gate), same convention MySparksContainer's own `if
            (user)` guard documents. Also requires showBoard -- see that flag's own comment above for
            why the pinned row and footer must never render underneath the Loader.

            Suppressed entirely when the viewer already appears among renderedResults (computed above,
            the SAME window the podium/rows render from) -- Figma's own pinned row is "you . rank 12",
            for someone OUTSIDE the fetched window, never a second copy of a row already on screen (a
            viewer inside the top 10 would otherwise render twice: once inline at their rank, again
            pinned directly below).

            `isViewer` swaps only the NAME text (and highlights the row) -- `InitialsAvatar` still gets
            viewerUser's own firstName/lastName below, so the initials stay the person's own. */}
        {showBoard && viewer && viewerUser && !viewerAlreadyVisible ? (
          <div className="overview-leaderboard-pinned" data-testid="overview-leaderboard-pinned-row">
            <RankRow
              rank={viewer.rank}
              firstName={viewerUser.first_name}
              lastName={viewerUser.last_name}
              imageUrl={viewerUser.avatar_url}
              totalScore={viewer.total_score}
              leaderScore={leaderScore}
              isViewer
            />
          </div>
        ) : null}
        {/* `!== null`, never `!viewer.points_to_next_rank`. `null` means rank 1 -- nobody to overtake --
            and is the ONLY value that suppresses this footer.
            The api cannot send `0` here: `points_to_next_rank` is what it takes to OVERTAKE the row
            above, not the raw gap to it, so a neighbour on the same score costs `1` (matching them only
            ties, and the board's id tiebreak then keeps the later account below). Since a neighbour
            above always scores >= the viewer, the minimum is 1. A falsy check would therefore be
            indistinguishable from this one TODAY -- which is exactly why it stays `!== null`: it keeps
            being right if a future window ever does yield 0, rather than silently hiding the footer.
            Also requires showBoard -- see that flag's own comment above. */}
        {showBoard && viewer && viewer.points_to_next_rank !== null ? (
          <div className="overview-leaderboard-footer" data-testid="overview-leaderboard-footer">
            {`${viewer.points_to_next_rank} and you move up a place`}
          </div>
        ) : null}
        {/* The footer's complementary case: the viewer has no score at all this board/period (the api's
            own `viewer: null`, same contract the pinned row above reads). Renders in the footer's OWN
            slot -- that footer already vanishes when `viewer` is null, so this fills space that was
            already empty rather than growing the card. Gated exactly like the pinned row above (same
            `showBoard`, same `viewerUser`, same `viewerAlreadyVisible`) with `viewer === null` in place
            of `viewer &&` -- deliberately NOT a separate load-failure guard, mirroring
            LeaderboardsContainer.tsx's own showViewerUnrankedState: this line and the pinned row are two
            renderings of the same `viewer` field, so they share gating rather than diverging on it.
            Mutually exclusive with the footer above by construction (`viewer === null` vs. `viewer &&`),
            never both. Short-form copy (leaderboardViewerUnrankedShortCopy) -- see its own JSDoc for why
            this card drops the tab's "your first Spark..." call to action. */}
        {showBoard && viewerUser && viewer === null && !viewerAlreadyVisible ? (
          <div className="overview-leaderboard-unranked" data-testid="overview-leaderboard-unranked">
            {leaderboardViewerUnrankedShortCopy(period)}
          </div>
        ) : null}
      </div>
    </ErrorBoundary>
  );
};

export default OverviewLeaderboard;
